import { leadStageNames, type LeadCard, type LeadStage } from '@smarton-pbx/shared';
import { language, locale, t } from '../../i18n';
import { formatNumber } from '../../lib/calls';

/** What a lead is called on its card: the company, else a person's name, else how to reach them. */
export function leadTitle(lead: Pick<LeadCard, 'company' | 'contacts'>): string {
  const first = lead.contacts[0];
  return (
    lead.company ?? lead.contacts.find((c) => c.name)?.name ?? (first?.e164 ? formatNumber(first.e164) : null) ?? first?.email ?? t('Lead')
  );
}

/** Today in the browser's time zone, as YYYY-MM-DD. */
export function localDay(offsetDays = 0, from = new Date()): string {
  const date = new Date(from.getFullYear(), from.getMonth(), from.getDate() + offsetDays);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** "26 Oct" („spalio 26 d.“), with the year when it isn't this one. */
export function formatDay(day: string): string {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  const value = new Date(year, month - 1, date);
  // Lithuanian has no short month names in dates (they come out as "10-26").
  return value.toLocaleDateString(locale, {
    day: 'numeric',
    month: language === 'lt' ? 'long' : 'short',
    ...(year !== new Date().getFullYear() && { year: 'numeric' }),
  });
}

/** A next call: "26 Oct", or "26 Oct 14:30" when it's at a time. */
export function formatNextCall(day: string, time: string | null): string {
  return time ? `${formatDay(day)} ${time}` : formatDay(day);
}

/** A next call: past its day (or its time today), today, or still to come. */
export function dueState(day: string, time: string | null = null): 'overdue' | 'today' | 'later' {
  const now = new Date();
  const today = localDay(0, now);
  const clock = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  return day < today || (day === today && time !== null && time < clock) ? 'overdue' : day === today ? 'today' : 'later';
}

/** Common places leads come from, to start with, then the team's own (each once). */
export function sourceSuggestions(used: string[]): string[] {
  const seen = new Set<string>();
  return [...used, t('Cold call'), 'Facebook', 'Instagram', 'Google', t('Website'), t('Referral')].filter((source) => {
    const key = source.toLocaleLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function StageDot({ stage }: { stage: LeadStage }) {
  return <span className={`stage-dot stage-${stage}`} aria-hidden />;
}

export function StageName({ stage }: { stage: LeadStage }) {
  return (
    <span className="stage-name">
      <StageDot stage={stage} />
      {t(leadStageNames[stage])}
    </span>
  );
}
